// SIPP-809: product-type → Klaviyo/CIO waitlist attribute mapping.
//
// The backend's /users/:id/events/waitlist-signup endpoint owns the source of
// truth for which attribute name actually gets written onto the Klaviyo + CIO
// profile — this helper exists so the frontend knows the human-friendly label
// for a given product type and can render it consistently. Keep in sync with
// the backend controller's internal map.

export type ProductType =
  | 'water_pitcher'
  | 'refrigerator_filter'
  | 'under_sink_system'
  | 'counter_top_filter'
  | 'whole_home_system'
  | 'smart_home_bundle'
  | 'laundry_addon'
  | 'shower_filter'
  | 'under_sink_carbon'
  | 'pitcher_upgrade'
  | 'lab_grade_test'
  // SIPP-813: equipment-picker options for the SIPP Pro waitlist card. These
  // overlap in spirit with the D3c product types above but are a distinct,
  // tighter set keyed to the picker UX ("what's your current water setup?").
  | 'pitcher'
  | 'undersink'
  | 'countertop'
  | 'ro'
  | 'whole_home'
  | 'no_equipment'

const PRODUCT_TYPE_TO_WAITLIST_ATTR: Record<ProductType, string> = {
  water_pitcher: 'pitcher_waitlist',
  refrigerator_filter: 'refrigerator_waitlist',
  under_sink_system: 'under_sink_waitlist',
  counter_top_filter: 'countertop_waitlist',
  whole_home_system: 'whole_home_waitlist',
  smart_home_bundle: 'smart_home_waitlist',
  laundry_addon: 'laundry_waitlist',
  shower_filter: 'shower_waitlist',
  under_sink_carbon: 'under_sink_carbon_waitlist',
  pitcher_upgrade: 'pitcher_upgrade_waitlist',
  lab_grade_test: 'lab_test_waitlist',
  // SIPP-813 picker attributes. These match the backend's
  // EQUIPMENT_PICKER_ATTRIBUTES whitelist in productRecommendations.ts.
  pitcher: 'pitcher_waitlist',
  undersink: 'undersink_waitlist',
  countertop: 'countertop_waitlist',
  ro: 'ro_waitlist',
  whole_home: 'whole_home_waitlist',
  no_equipment: 'no_equipment_waitlist',
}

const PRODUCT_TYPES = Object.keys(PRODUCT_TYPE_TO_WAITLIST_ATTR) as ProductType[]

export const isProductType = (value: string): value is ProductType =>
  (PRODUCT_TYPES as readonly string[]).includes(value)

export const productTypeToWaitlistAttribute = (type: ProductType): string =>
  PRODUCT_TYPE_TO_WAITLIST_ATTR[type]

// SIPP-813: ordered options for the equipment-setup picker shown inside the
// SIPP Pro waitlist card. Label copy mirrors the ticket UX.
export interface EquipmentPickerOption {
  value: Extract<
    ProductType,
    'pitcher' | 'undersink' | 'countertop' | 'ro' | 'whole_home' | 'no_equipment'
  >
  label: string
}

export const EQUIPMENT_PICKER_OPTIONS: readonly EquipmentPickerOption[] = [
  { value: 'pitcher', label: 'Pitcher (Brita, ZeroWater, etc.)' },
  { value: 'undersink', label: 'Under-sink filter' },
  { value: 'countertop', label: 'Countertop filter' },
  { value: 'ro', label: 'Reverse osmosis' },
  { value: 'whole_home', label: 'Whole-home filter' },
  { value: 'no_equipment', label: 'None yet' },
]
